/**
 * @file InterviewRespondPage.jsx
 * @description Public, logged-out page behind the one-tap buttons in interview
 *   invite emails: `abacases.com/interview-respond?<signed params>`.
 *
 * ## Why the UI is here and not in the edge function
 * The edge-function gateway rewrites `Content-Type` to `text/plain` and adds
 * `X-Content-Type-Options: nosniff` on every response, so a function physically
 * cannot serve an HTML page — a browser shows the markup as source. Independent
 * of that, a signed link pointing at `<ref>.supabase.co` in a therapist's inbox
 * reads as phishing, and `abacases.com` reads as the product.
 *
 * ## This page has no authority
 * It renders what the server tells it and nothing more. The signature is
 * verified server-side on BOTH calls:
 *   • GET  — preview: what action, which time, where the invite stands. No writes.
 *   • POST — the single button on this page. Performs the action.
 * A tampered or forged link renders the same "not valid" screen no matter what
 * the query string claims, because the page never evaluates the signature.
 *
 * ## Why GET can't perform the action
 * Mail scanners (Outlook Safe Links, Proofpoint, Gmail) fetch every URL in an
 * email before a human sees it. If landing here confirmed a slot, a robot would
 * book interviews on people's behalf. Landing here only describes.
 *
 * ## Logged out by design
 * Mounted from App.js ahead of every auth/onboarding/legal gate, so it renders
 * for someone with no session, on a phone, in an email client's in-app browser.
 * It never signs anyone in — mail gets forwarded, and agency addresses like
 * `info@` are shared inboxes.
 *
 * The route is deliberately absent from src/data/seoRoutes.json: not
 * prerendered, not in the sitemap, and noindex'd at runtime.
 *
 * @module components/InterviewRespondPage
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import { Btn } from "./UI";
import Logo from "./Logo";
import { CheckCircleIcon, AlertTriangleIcon, InfoIcon, ClockIcon } from "./Icons";
import { usePageSeo } from "../lib/seo";
import { readActionParams, callInterviewRespond } from "../lib/interviewRespond";

/** Format a tz-naive slot in the reader's language. Server sends raw date/time. */
function fmtSlot(slot, lang) {
  if (!slot || !slot.date) return "";
  let ds = slot.date;
  try {
    const d = new Date(`${slot.date}T00:00:00`);
    if (!isNaN(d.getTime())) {
      ds = d.toLocaleDateString(lang === "es" ? "es-US" : "en-US", {
        weekday: "long", month: "long", day: "numeric",
      });
    }
  } catch { /* keep the raw ISO date */ }
  return slot.time ? `${ds} · ${slot.time}` : ds;
}

/**
 * Suppress the Referer for as long as this page is mounted, so the signed URL
 * in the address bar cannot leak to any host we talk to. The global policy
 * (strict-origin-when-cross-origin) still sends the full URL on same-origin
 * requests, which is exactly the case this closes.
 */
function useNoReferrer() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const el = document.createElement("meta");
    el.setAttribute("name", "referrer");
    el.setAttribute("content", "no-referrer");
    document.head.appendChild(el);
    return () => { try { document.head.removeChild(el); } catch { /* already gone */ } };
  }, []);
}

export default function InterviewRespondPage() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").startsWith("es") ? "es" : "en";

  // Never indexed, never followed. Belt and braces alongside the route being
  // absent from seoRoutes.json (so it is neither prerendered nor sitemapped).
  usePageSeo({ title: t("interviewRespond.pageTitle"), noindex: true });
  useNoReferrer();

  // Read once on mount. The params are never re-derived from state, and never
  // rewritten — a refresh must still work.
  const [params] = useState(() =>
    (typeof window === "undefined" ? null : readActionParams(window.location.search)));

  const [phase, setPhase] = useState(params ? "loading" : "preview"); // loading | preview | submitting
  const [data,  setData]  = useState(params ? null : { ok: false, state: "invalid", reason: "missing" });

  const load = useCallback(() => {
    if (!params) return;
    setPhase("loading");
    callInterviewRespond("GET", params).then((res) => {
      setData(res);
      setPhase("preview");
    });
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const submit = useCallback(() => {
    if (!params) return;
    setPhase("submitting");
    callInterviewRespond("POST", params).then((res) => {
      setData(res);
      setPhase("preview");
    });
  }, [params]);

  const state  = data?.state || "loading";
  const action = data?.action || "";
  const inv    = data?.invite || {};

  // ── One screen description, derived from (state, action). ──────────────────
  // tone drives the accent colour; `button` present ⇒ this screen can act.
  let s;
  if (phase === "loading") {
    s = { tone: "info", icon: ClockIcon, title: t("interviewRespond.loadingTitle") };
  } else if (state === "network" || state === "error") {
    s = {
      tone: "warn", icon: AlertTriangleIcon,
      title: state === "network" ? t("interviewRespond.networkTitle") : t("interviewRespond.errorTitle"),
      body:  state === "network" ? t("interviewRespond.networkBody")  : t("interviewRespond.errorBody"),
      retry: true,
    };
  } else if (state === "expired") {
    s = {
      tone: "info", icon: ClockIcon,
      title: t("interviewRespond.expiredTitle"), body: t("interviewRespond.expiredBody"),
      next: { href: "/applications", label: t("interviewRespond.ctaOpenApp") },
    };
  } else if (state === "invalid") {
    const r = data?.reason;
    s = {
      tone: "warn", icon: AlertTriangleIcon,
      title:
        r === "slot_gone" ? t("interviewRespond.slotGoneTitle")
        : r === "link_outdated" ? t("interviewRespond.linkOutdatedTitle")
        : r === "not_found" ? t("interviewRespond.notFoundTitle")
        : t("interviewRespond.invalidTitle"),
      body:
        r === "slot_gone" ? t("interviewRespond.slotGoneBody")
        : r === "link_outdated" ? t("interviewRespond.linkOutdatedBody")
        : r === "not_found" ? t("interviewRespond.notFoundBody")
        : t("interviewRespond.invalidBody"),
      next: { href: "/applications", label: t("interviewRespond.ctaOpenApp") },
    };
  } else if (state === "already_confirmed") {
    const same = inv.sameSlot !== false;
    s = {
      tone: "ok", icon: CheckCircleIcon,
      title: same ? t("interviewRespond.alreadyConfirmedTitle") : t("interviewRespond.alreadyScheduledTitle"),
      body:  same ? t("interviewRespond.alreadyConfirmedBody")  : t("interviewRespond.alreadyScheduledBody"),
      slot: inv.slot,
      next: { href: "/applications", label: t("interviewRespond.ctaViewInterview") },
    };
  } else if (state === "closed") {
    const recorded = String(data?.reason || "").startsWith("outcome_recorded");
    s = recorded
      ? {
          tone: "ok", icon: CheckCircleIcon,
          title: t("interviewRespond.alreadyRecordedTitle"),
          body: data.reason === "outcome_recorded_completed"
            ? t("interviewRespond.alreadyRecordedCompletedBody")
            : t("interviewRespond.alreadyRecordedBody"),
          next: { href: "/applications", label: t("interviewRespond.ctaOpenApp") },
        }
      : {
          tone: "info", icon: InfoIcon,
          title: t("interviewRespond.closedTitle"),
          body: t("interviewRespond.closedBody", {
            status: t(`interviewRespond.status_${inv.status}`, {
              defaultValue: t("interviewRespond.statusClosedFallback"),
            }),
          }),
          next: { href: "/applications", label: t("interviewRespond.ctaOpenApp") },
        };
  } else if (state === "done") {
    const r = data?.result;
    s = r === "declined"
      ? { tone: "info", icon: CheckCircleIcon, title: t("interviewRespond.doneDeclinedTitle"), body: t("interviewRespond.doneDeclinedBody") }
      : r === "new_times_requested"
      ? { tone: "info", icon: CheckCircleIcon, title: t("interviewRespond.doneNoneTitle"), body: t("interviewRespond.doneNoneBody") }
      : r === "outcome_recorded"
      ? { tone: "ok", icon: CheckCircleIcon, title: t("interviewRespond.doneOutcomeTitle"), body: t("interviewRespond.doneOutcomeBody") }
      : { tone: "ok", icon: CheckCircleIcon, title: t("interviewRespond.doneConfirmedTitle"), body: t("interviewRespond.doneConfirmedBody"), slot: inv.slot };
    // Exactly one next step, and it is the useful one: more work near them.
    s.next = { href: "/aba-case-finder", label: t("interviewRespond.ctaFindCases") };
  } else if (state === "ready" && action === "confirm") {
    s = {
      tone: "ok", icon: CheckCircleIcon,
      title: t("interviewRespond.confirmTitle"), body: t("interviewRespond.confirmBody"),
      slot: inv.slot, button: t("interviewRespond.confirmBtn"), variant: "success",
    };
  } else if (state === "ready" && action === "decline") {
    s = {
      tone: "warn", icon: AlertTriangleIcon,
      title: t("interviewRespond.declineTitle"), body: t("interviewRespond.declineBody"),
      button: t("interviewRespond.declineBtn"), variant: "danger",
    };
  } else if (state === "ready" && action === "none") {
    s = {
      tone: "info", icon: InfoIcon,
      title: t("interviewRespond.noneTitle"), body: t("interviewRespond.noneBody"),
      button: t("interviewRespond.noneBtn"), variant: "primary",
    };
  } else if (state === "ready" && action === "outcome") {
    const answer = t(`interviewRespond.outcome_${data?.arg}`, { defaultValue: "" });
    s = {
      tone: "info", icon: InfoIcon,
      title: t("interviewRespond.outcomeTitle"), body: t("interviewRespond.outcomeBody"),
      answer, button: t("interviewRespond.outcomeBtn"), variant: "primary",
    };
  } else {
    s = {
      tone: "warn", icon: AlertTriangleIcon,
      title: t("interviewRespond.invalidTitle"), body: t("interviewRespond.invalidBody"),
      next: { href: "/applications", label: t("interviewRespond.ctaOpenApp") },
    };
  }

  const accent = s.tone === "warn" ? T.red : s.tone === "info" ? T.blue : T.green;
  const Icon = s.icon;
  const busy = phase === "loading" || phase === "submitting";
  const slotText = fmtSlot(s.slot, lang);
  const meta = [inv.format, inv.duration ? t("interviewRespond.durationMin", { n: inv.duration }) : ""]
    .filter(Boolean).join(" · ");

  return (
    <div style={{
      minHeight: "100vh", background: T.bg, fontFamily: T.fontBody, color: T.text,
      padding: "32px 16px calc(48px + env(safe-area-inset-bottom, 0px))",
      display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      <div style={{ marginBottom: 20 }}>
        <Logo variant="lockup" height={30} alt="ABAcases" />
      </div>

      <div style={{
        width: "100%", maxWidth: 460, background: T.surface,
        border: `1px solid ${T.border}`, borderRadius: T.radiusLg,
        boxShadow: T.shadow, padding: "26px 22px 24px", boxSizing: "border-box",
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: T.radiusFull, background: `${accent}14`,
          display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14,
        }}>
          <Icon size={20} color={accent} />
        </div>

        <h1 style={{
          margin: 0, fontSize: 20, lineHeight: 1.3, fontWeight: 800,
          letterSpacing: "-0.02em", color: T.text,
        }}>
          {s.title}
        </h1>

        {inv.caseLabel && (
          <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600, color: T.textLight }}>
            {inv.caseLabel}
          </div>
        )}

        {s.body && (
          <p style={{ margin: "12px 0 0", fontSize: 14.5, lineHeight: 1.6, color: T.textMid }}>
            {s.body}
          </p>
        )}

        {(slotText || s.answer) && (
          <div style={{
            marginTop: 16, padding: "13px 15px", background: T.surfaceSunk,
            borderRadius: T.radiusMd, border: `1px solid ${T.borderSoft}`,
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>
              {slotText || s.answer}
            </div>
            {slotText && meta && (
              <div style={{ marginTop: 4, fontSize: 12.5, color: T.textLight }}>{meta}</div>
            )}
          </div>
        )}

        {s.button && (
          <div style={{ marginTop: 20 }}>
            <Btn
              variant={s.variant}
              onClick={submit}
              disabled={busy}
              style={{ width: "100%", padding: "13px 20px", fontSize: 15.5, borderRadius: T.radiusMd }}
            >
              {phase === "submitting" ? t("interviewRespond.submitting") : s.button}
            </Btn>
          </div>
        )}

        {s.retry && (
          <div style={{ marginTop: 20 }}>
            <Btn
              variant="outline"
              onClick={load}
              disabled={busy}
              style={{ width: "100%", padding: "13px 20px", fontSize: 15.5, borderRadius: T.radiusMd }}
            >
              {t("interviewRespond.retry")}
            </Btn>
          </div>
        )}

        {s.next && (
          /* rel="noreferrer" so the signed URL never rides along as a Referer. */
          <a
            href={s.next.href}
            rel="noreferrer"
            style={{
              display: "inline-block", marginTop: 18, fontSize: 14,
              fontWeight: 700, color: T.blue, textDecoration: "none",
            }}
          >
            {s.next.label} →
          </a>
        )}
      </div>

      <div style={{ marginTop: 18, fontSize: 12, color: T.textLight, textAlign: "center", maxWidth: 460 }}>
        {t("interviewRespond.footerNote")}
      </div>
    </div>
  );
}
